import { useCallback, useEffect, useState } from 'react'
import { Cross2Icon } from '@radix-ui/react-icons'
import {
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useTranslation } from '@/hooks/use-translation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DataTableFacetedFilter,
  DataTableViewOptions,
  DataTablePagination,
} from '@/components/crud'
import { useActiveCampaigns } from '@/features/minigames/hooks/use-campaigns'
import { createGameRecordColumns } from './game-records-columns'
import { useGameRecords } from './hooks/use-game-records'

export function GameRecordsTable() {
  const [rowSelection, setRowSelection] = useState({})
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [sorting, setSorting] = useState<SortingState>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')

  const { t } = useTranslation()

  // Fetch campaigns for filter options
  const { campaigns: campaignOptions } = useActiveCampaigns()

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTerm])

  // Reset pagination when search or filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearchTerm, columnFilters, sorting])

  // Calculate pagination parameters
  const skip = (currentPage - 1) * pageSize

  // Extract filters from table state
  const campaignFilter = columnFilters.find(
    (filter) => filter.id === 'campaignId'
  )?.value as string[] | undefined

  const selectedCampaignId = campaignFilter?.[0] // Take first selected campaign

  // Fetch game records data only if campaign is selected
  const {
    records: data,
    loading,
    error,
  } = useGameRecords({
    limit: pageSize,
    skip,
    guestPhone: debouncedSearchTerm || undefined,
    campaignId: selectedCampaignId,
  })

  // Handle search changes
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value)
  }, [])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setCurrentPage(1)
  }

  // Create columns with proper handlers
  const columns = createGameRecordColumns(t, campaignOptions)

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    pageCount: -1,
  })

  const isFiltered =
    table.getState().columnFilters.length > 0 || searchTerm.length > 0

  // Player type options for filtering
  const playerTypeOptions = [
    { label: t('gameRecords.playerTypes.user'), value: 'USER' },
    { label: t('gameRecords.playerTypes.guest'), value: 'GUEST' },
  ]

  // Show prompt to select campaign if none is selected
  if (!selectedCampaignId) {
    return (
      <div className='space-y-4'>
        {/* Toolbar */}
        <div className='flex items-center justify-between'>
          <div className='flex flex-1 items-center space-x-2'>
            {table.getColumn('campaignId') && (
              <DataTableFacetedFilter
                column={table.getColumn('campaignId')}
                title={t('gameRecords.campaign')}
                options={campaignOptions.map((campaign) => ({
                  label: campaign.name,
                  value: campaign.id,
                }))}
                showCount={false}
                maxVisible={5}
                searchable={true}
                clearable={true}
              />
            )}
          </div>
          <DataTableViewOptions
            table={table}
            label={t('common.view')}
            hideOnMobile={true}
            columnLabels={{
              guestName: t('gameRecords.player'),
              guestPhone: t('gameRecords.phone'),
              playerType: t('gameRecords.playerType'),
              prize: t('gameRecords.prize'),
              score: t('gameRecords.score'),
              campaignId: t('gameRecords.campaign'),
              createdAt: t('common.createdAt'),
            }}
          />
        </div>

        {/* Campaign selection prompt */}
        <div className='rounded-md border'>
          <div className='flex h-32 flex-col items-center justify-center space-y-2'>
            <p className='text-center text-muted-foreground'>
              {t('gameRecords.selectCampaignToViewRecords')}
            </p>
            <p className='text-center text-sm text-muted-foreground'>
              {t('gameRecords.chooseCampaignFromFilterAbove')}
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='rounded-md border'>
        <div className='flex h-24 items-center justify-center'>
          <p className='text-destructive'>
            {t('gameRecords.errorLoadingRecords')} {String(error)}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className='space-y-4'>
      {/* Toolbar */}
      <div className='flex items-center justify-between'>
        <div className='flex flex-1 items-center space-x-2'>
          {table.getColumn('campaignId') && (
            <DataTableFacetedFilter
              column={table.getColumn('campaignId')}
              title={t('gameRecords.campaign')}
              options={campaignOptions.map((campaign) => ({
                label: campaign.name,
                value: campaign.id,
              }))}
              showCount={false}
              maxVisible={5}
              searchable={true}
              clearable={true}
            />
          )}
          <Input
            placeholder={t('gameRecords.filterRecords')}
            value={searchTerm}
            onChange={(event) => handleSearchChange(event.target.value)}
            className='h-8 w-[150px] lg:w-[250px]'
          />
          {table.getColumn('playerType') && (
            <DataTableFacetedFilter
              column={table.getColumn('playerType')}
              title={t('gameRecords.playerType')}
              options={playerTypeOptions}
              showCount={true}
              maxVisible={2}
              searchable={true}
              clearable={true}
            />
          )}
          {isFiltered && (
            <Button
              variant='ghost'
              onClick={() => {
                table.resetColumnFilters()
                setSearchTerm('')
              }}
              className='h-8 px-2 lg:px-3'
            >
              {t('common.reset')}
              <Cross2Icon className='ml-2 h-4 w-4' />
            </Button>
          )}
        </div>
        <DataTableViewOptions
          table={table}
          label={t('common.view')}
          hideOnMobile={true}
          columnLabels={{
            guestName: t('gameRecords.player'),
            guestPhone: t('gameRecords.phone'),
            playerType: t('gameRecords.playerType'),
            prize: t('gameRecords.prize'),
            score: t('gameRecords.score'),
            campaignId: t('gameRecords.campaign'),
            createdAt: t('common.createdAt'),
          }}
        />
      </div>

      {/* Table */}
      <div className='rounded-md border'>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      className={header.column.columnDef.meta?.className ?? ''}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className='h-24 text-center'
                >
                  {t('gameRecords.loadingRecords')}
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className='group/row'
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cell.column.columnDef.meta?.className ?? ''}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className='h-24 text-center'
                >
                  {t('gameRecords.noRecords')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <DataTablePagination
        table={table}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={[10, 20, 50, 100]}
        showRowsSelected={true}
      />
    </div>
  )
}
